// Side-by-side diff of the patient's final note (left) against a freshly
// generated test note (right), shown after a "Test Note" run completes.
//
// Same presentation as the Templates "Compare Versions" modal — it renders the
// shared SplitDiffView, so the aligned columns, word-level highlighting, swap
// toggle and prev/next diff navigation all behave identically. Only the column
// headers differ: the test note is identified by prompt version and job name
// rather than by template version, and the final note carries neither.

import React, { useEffect, useState } from "react";
import { Button } from "../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/Dialog";
import { Check, GitCompare, Loader2 } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import SplitDiffView from "../Templates/SplitDiffView";
import { setTemplateVersionAsDefault } from "./testNoteApi";

const ColumnHeader = ({ title, subtitle, jobName }) => (
  <div className="min-w-0">
    <div className="flex items-center gap-2">
      <span className="text-lg font-semibold text-gray-900">{title}</span>
      {subtitle && (
        <span className="text-sm text-gray-500 truncate">{subtitle}</span>
      )}
    </div>
    {jobName && (
      <div className="text-xs text-gray-400 mt-0.5 font-mono truncate">
        {jobName}
      </div>
    )}
  </div>
);

const TestNoteCompareModal = ({
  open,
  onOpenChange,
  patientId,
  originalNote,
  testNote,
  generationError,
  convPromptName,
  convPromptVersion,
  testJobName,
  currentDefaultVersion,
  onDefaultSwitched,
}) => {
  const { toast } = useToast();

  // The swap button in SplitDiffView flips which note sits on each side.
  const [swapped, setSwapped] = useState(false);

  // Promoting the tested version to default changes what the production
  // pipeline generates for every subsequent note, so it takes a second click.
  const [confirmingSwitch, setConfirmingSwitch] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchedTo, setSwitchedTo] = useState(null);

  // Reset the swap whenever a fresh pair is opened.
  useEffect(() => {
    setSwapped(false);
  }, [originalNote, testNote, open]);

  // Drop any half-finished confirm when a different pair is loaded.
  useEffect(() => {
    setConfirmingSwitch(false);
    setSwitchedTo(null);
  }, [testJobName, open]);

  const isAlreadyDefault =
    !!convPromptVersion && convPromptVersion === currentDefaultVersion;
  const canSwitchDefault =
    !!convPromptName && !!convPromptVersion && !isAlreadyDefault;

  const handleSwitchDefault = async () => {
    if (!canSwitchDefault || switching) return;

    if (!confirmingSwitch) {
      setConfirmingSwitch(true);
      return;
    }

    setSwitching(true);
    const result = await setTemplateVersionAsDefault(
      convPromptName,
      convPromptVersion
    );
    setSwitching(false);
    setConfirmingSwitch(false);

    if (!result.success) {
      toast({
        variant: "destructive",
        title: "Could not switch default",
        description: result.error,
      });
      return;
    }

    setSwitchedTo(convPromptVersion);
    toast({
      title: "Default switched",
      description: `${convPromptName} now defaults to ${convPromptVersion}.`,
    });
    onDefaultSwitched?.(convPromptVersion);
  };

  const switchDefaultLabel = () => {
    if (switching) return "Switching…";
    if (switchedTo) return `${switchedTo} is now default`;
    if (isAlreadyDefault) return "Already the default";
    if (confirmingSwitch) return `Confirm — make ${convPromptVersion} default`;
    return "Switch defaults";
  };

  const originalColumn = {
    title: "Final note",
    subtitle: "as shown in Review Note",
  };
  const testColumn = {
    title: "Test note",
    subtitle: convPromptVersion
      ? `${convPromptName} · ${convPromptVersion}`
      : "workflow default prompt",
    jobName: testJobName,
  };

  const left = swapped ? testColumn : originalColumn;
  const right = swapped ? originalColumn : testColumn;
  const leftText = swapped ? testNote : originalNote;
  const rightText = swapped ? originalNote : testNote;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[1500px] h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-3xl font-semibold text-gray-900 flex items-center gap-3 leading-none">
            <GitCompare className="h-8 w-8 shrink-0" />
            <span className="leading-none">Compare Test Note</span>
          </DialogTitle>
          <DialogDescription className="text-xl text-gray-700 mt-2">
            Patient {patientId}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-0 border-b border-gray-200 pb-2">
          <div className="pr-4">
            <ColumnHeader {...left} />
          </div>
          <div className="pl-4">
            <ColumnHeader {...right} />
          </div>
        </div>

        {generationError && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <p className="font-medium">
              Test note generation failed. The test note shown may be the
              pipeline's fallback, not a freshly generated note.
            </p>
            <p className="font-mono text-xs mt-1">{generationError}</p>
          </div>
        )}

        <SplitDiffView
          open={open}
          leftText={leftText}
          rightText={rightText}
          onToggleSwap={() => setSwapped((s) => !s)}
          identicalMessage="The test note is identical to the current note."
        />

        <DialogFooter className="flex items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSwitchDefault}
              disabled={!canSwitchDefault || switching || !!switchedTo}
              title={
                canSwitchDefault
                  ? `Make ${convPromptVersion} the default version of ${convPromptName}`
                  : "Available once a specific prompt version has been tested"
              }
              className="bg-ktx-purple hover:bg-purple-700 text-white disabled:opacity-50"
            >
              {switching && <Loader2 className="h-4 w-4 animate-spin" />}
              {switchedTo && <Check className="h-4 w-4" />}
              {switchDefaultLabel()}
            </Button>
            {confirmingSwitch && !switching && (
              <span className="text-sm text-gray-600">
                Every new note will use this version.
              </span>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="text-lg px-6 py-3 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TestNoteCompareModal;
